import * as React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { DebugFragment } from './DebugFragment';
import { IObservableValue } from 'mobx';
import { observer } from 'mobx-react';

import { WhiteSpacePre } from '../Helper/StyleHelper';
import { showDebugInfo } from '../Helper/Debug';

import { errorTrackingEnabled, ErrorWithRavenOptions, getLastErrorId } from '@egr/xbox/utils/Error';

interface ErrorInfoBaseProps {
    error: Error;
    ignoreCallback?: VoidFunction;
    userMode: boolean;
}

interface ErrorInfoProps {
    error: Error;
    ignoreCallback?: VoidFunction;
    userMode?: IObservableValue<boolean>;
}

export const ErrorInfo: React.FC<ErrorInfoProps> = observer(
    (props: ErrorInfoProps) => {
        const { error, ignoreCallback, userMode } = props;

        return <ErrorInfoBase error={error} ignoreCallback={ignoreCallback} userMode={userMode?.get() === true}/>;
    }
);

export class ErrorInfoBase extends React.PureComponent<ErrorInfoBaseProps> {
    public override render(): React.ReactNode {
        if (
            this.props.userMode && showDebugInfo.get() === false) {
            return this.getContent();
        }

        return (
            <DebugFragment>
                {this.getContent()}
                <Typography key="ignore-callback"><Button color="primary" onClick={this.props.ignoreCallback}>Ignore error</Button></Typography>
            </DebugFragment>
        );
    }

    public getContent(): React.ReactNode {
        return (
            <React.Fragment>
                <Typography key="space">&nbsp;</Typography>
                {
                    this.getSentryEventId()
                }
                {
                    this.getTypo('Error name', this.props.error.name)
                }
                {
                    this.getTypo('Error message', this.props.error.message)
                }
                {
                    this.getExtraData()
                }
                {
                    this.props.error.stack ? this.getTypo('Error stack', this.props.error.stack) : null
                }
            </React.Fragment>
        );
    }

    protected getTypo(label: string, value: React.ReactNode): React.ReactNode {
        return (
            <React.Fragment>
                <Typography variant="caption" color="error">{label}</Typography>
                <Typography paragraph={true}>{value}</Typography>
            </React.Fragment>
        );
    }

    protected getSentryEventId(): React.ReactNode {
        const errorId: string | undefined = getLastErrorId();
        if (!errorTrackingEnabled() || errorId == null) {
            return null;
        }

        return this.getTypo('Last sentry event', errorId);
    }

    protected getExtraData(): React.ReactNode {
        const error: ErrorWithRavenOptions = this.props.error;
        if (error.ravenOptions == null) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return this.getTypo('Raven extra options', <span style={WhiteSpacePre as any}>{JSON.stringify(error.ravenOptions, undefined, 4)}</span>);
    }
}